import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { FaStar, FaTimes, FaLock, FaCheck } from "react-icons/fa";
import { getAllModes } from "../../api/mode";
import { getMyCustomModes } from "../../api/custom-mode";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { Button, Input, Modal, Spinner } from "../../ui";

const MAX_MODES = 6;
const FREE_CUSTOM_PER_GAME = 1;
const CUSTOM_PAGE_SIZE = 12;

// Card for a single mode in the grid. Shows the app's "games left" badge:
// community modes → remaining-games count (bottom-left); official modes → percent
// played (top-left). Tapping the badge opens an info popup (doesn't toggle).
const ModeCard = ({ mode, isCustom, selected, locked, disabled, onClick, onInfo }) => {
  const gamesLeft = mode.gamesLeft;
  const totalGames = mode.totalGames || 0;
  const hasGamesLeft = gamesLeft !== undefined && gamesLeft !== null;
  const pctPlayed =
    totalGames > 0
      ? Math.max(0, Math.min(100, Math.round(100 - (gamesLeft / totalGames) * 100)))
      : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`relative rounded-xl overflow-hidden border-[3px] aspect-[3/4] bg-header transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-header ${
        selected
          ? "border-primary shadow-[0_0_0_3px_rgb(var(--c-primary)/0.25)]"
          : "border-white/15"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-white/35"}`}
    >
      <img src={mode.image} alt={mode.name} className="w-full h-3/4 object-cover" />
      <div className="h-1/4 flex items-center justify-center px-1 bg-header border-t border-white/10">
        <span className="text-white text-xs font-bold text-center line-clamp-2">
          {mode.name}
        </span>
      </div>

      {selected && (
        <div className="absolute top-1.5 right-1.5 bg-primary rounded-full w-6 h-6 flex items-center justify-center shadow">
          <FaCheck className="text-header text-xs" aria-hidden="true" />
        </div>
      )}

      {!locked && hasGamesLeft && (
        <span
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            onInfo();
          }}
          className={`absolute bg-header/85 border border-white/15 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 hover:bg-header ${
            isCustom ? "bottom-[27%] left-1" : "top-1.5 left-1.5"
          }`}
        >
          {isCustom ? (
            <>
              {gamesLeft} <span className="opacity-80 font-normal">لعبة</span>
            </>
          ) : (
            `${pctPlayed}%`
          )}
        </span>
      )}

      {locked && (
        <div className="absolute inset-0 bg-header/75 flex flex-col items-center justify-center gap-1 p-2">
          <FaLock className="text-primary" aria-hidden="true" />
          <span className="text-white text-[11px] font-semibold text-center">
            متاحة للمشتركين
          </span>
        </div>
      )}
    </button>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="text-white/55">{label}</span>
    <b className="text-white">{value}</b>
  </div>
);

const ModePicker = ({ gameInfo, setGameInfo }) => {
  const { isPremium, isFreeTier, showUpgradeModal } = useSubscription();
  const [activeTab, setActiveTab] = useState("modes"); // modes | favorites | custom
  const [search, setSearch] = useState("");
  const [customQ, setCustomQ] = useState(""); // debounced server-side search
  const [infoMode, setInfoMode] = useState(null); // { ...mode, isCustom }

  // Debounce the search box into the server-side query for community modes.
  useEffect(() => {
    const t = setTimeout(() => setCustomQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: modes, isLoading: modesLoading } = useQuery({
    queryKey: ["modes"],
    queryFn: getAllModes,
  });

  // Community modes: paginated + server-side search, so it never bulk-loads.
  const {
    data: customPages,
    isLoading: customLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["myCustomModes", "picker", customQ],
    queryFn: ({ pageParam = 1 }) =>
      getMyCustomModes({ page: pageParam, limit: CUSTOM_PAGE_SIZE, q: customQ }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.totalPages
        ? last.pagination.page + 1
        : undefined,
  });
  const customModes = (customPages?.pages || []).flatMap((p) => p.data);

  // Infinite scroll for the community tab.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || activeTab !== "custom" || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const total =
    (gameInfo.modes?.length || 0) + (gameInfo.customModes?.length || 0);

  // Lookup so selected chips can show name/image regardless of active tab.
  const lookup = useMemo(() => {
    const map = {};
    (modes || []).forEach((m) => (map[m._id] = { ...m, isCustom: false }));
    (customModes || []).forEach((m) => (map[m._id] = { ...m, isCustom: true }));
    return map;
  }, [modes, customModes]);

  const toggle = (mode, isCustom) => {
    const id = mode._id;
    const selectedList = isCustom ? gameInfo.customModes : gameInfo.modes;
    const isSelected = selectedList.includes(id);

    if (!isSelected) {
      // Locked official mode -> premium only.
      if (!isCustom && mode.locked && !isPremium) {
        showUpgradeModal({ messageKey: "PREMIUM_MODE" });
        return;
      }
      // Free tier: max 1 community mode per game.
      if (
        isCustom &&
        !isPremium &&
        (gameInfo.customModes?.length || 0) >= FREE_CUSTOM_PER_GAME
      ) {
        showUpgradeModal({ messageKey: "CUSTOM_MODES_LIMIT" });
        return;
      }
      if (total >= MAX_MODES) return; // hard cap, button hint covers messaging
    }

    setGameInfo((prev) => {
      const key = isCustom ? "customModes" : "modes";
      const list = prev[key];
      return {
        ...prev,
        [key]: isSelected ? list.filter((x) => x !== id) : [...list, id],
      };
    });
  };

  const filterByName = (list) =>
    (list || []).filter((m) => (m.name || "").includes(search));

  const favorites = (modes || []).filter((m) => m.isFavorited);

  // Mirror the app's official-mode ordering (ModesList.js `sortModes`): for the
  // free tier, locked (premium) modes sort to the end; then modes with 0 games
  // left sort to the end.
  const sortModes = (a, b) => {
    if (isFreeTier) {
      if (a?.locked && !b?.locked) return 1;
      if (!a?.locked && b?.locked) return -1;
    }
    if (a?.gamesLeft === 0 && b?.gamesLeft !== 0) return 1;
    if (a?.gamesLeft !== 0 && b?.gamesLeft === 0) return -1;
    return 0;
  };
  const sortFilter = (list) => [...filterByName(list)].sort(sortModes);
  // Categorization (app): مخصصة = subMode modes, أساسية = the rest.
  const subModes = (modes || []).filter((m) => m?.subMode);
  const mainModes = (modes || []).filter((m) => !m?.subMode);

  const renderGrid = (list, isCustom, { serverFiltered = false } = {}) => {
    const filtered = serverFiltered ? list || [] : filterByName(list);
    if (!filtered.length) {
      return (
        <p className="text-white/55 text-center py-8 col-span-full">
          لا توجد فئات
        </p>
      );
    }
    return filtered.map((mode) => {
      const id = mode._id;
      const selectedList = isCustom ? gameInfo.customModes : gameInfo.modes;
      const isSelected = selectedList.includes(id);
      const locked = !isCustom && mode.locked && !isPremium;
      const disabled = !isSelected && total >= MAX_MODES && !locked;
      return (
        <ModeCard
          key={id}
          mode={mode}
          isCustom={isCustom}
          selected={isSelected}
          locked={locked}
          disabled={disabled}
          onClick={() => toggle(mode, isCustom)}
          onInfo={() => setInfoMode({ ...mode, isCustom })}
        />
      );
    });
  };

  const loading = activeTab === "custom" ? customLoading : modesLoading;

  const TabButton = ({ id, label, children }) => (
    <button
      type="button"
      onClick={() => setActiveTab(id)}
      aria-pressed={activeTab === id}
      aria-label={label}
      className={`flex-1 h-10 rounded-lg text-sm font-bold transition-colors ${
        activeTab === id
          ? "bg-white/15 text-white"
          : "text-white/65 hover:text-white hover:bg-white/5"
      }`}
    >
      {children}
    </button>
  );

  // Selected mode chips
  const selectedIds = [
    ...(gameInfo.modes || []).map((id) => ({ id, isCustom: false })),
    ...(gameInfo.customModes || []).map((id) => ({ id, isCustom: true })),
  ];

  return (
    <div>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedIds.map(({ id, isCustom }) => {
            const m = lookup[id];
            return (
              <span
                key={id}
                className="bg-mainButton/15 border border-mainButton/40 text-white text-xs font-semibold rounded-full ps-3 pe-1.5 py-1 flex items-center gap-1.5"
              >
                {m?.name || "..."}
                <button
                  type="button"
                  onClick={() => toggle({ _id: id }, isCustom)}
                  className="p-0.5 rounded-full text-white/60 hover:text-white hover:bg-white/10"
                  aria-label={`إزالة ${m?.name || "الفئة"}`}
                >
                  <FaTimes size={10} aria-hidden="true" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <Input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="ابحث عن فئة..."
        aria-label="ابحث عن فئة"
        className="mb-3"
      />

      <div className="flex gap-1 p-1 rounded-xl bg-white/[0.05] border border-white/10 mb-4">
        <TabButton id="modes">فئات تربع</TabButton>
        <TabButton id="favorites" label="المفضلة">
          <FaStar className="inline text-primary" aria-hidden="true" />
        </TabButton>
        <TabButton id="custom">فئات المجتمع</TabButton>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-white/80">
          <Spinner size={24} />
        </div>
      ) : activeTab === "modes" ? (
        // Official modes: free tier = one combined sorted grid (unlocked first);
        // premium = two sorted sections (مخصصة / أساسية) — mirrors the app.
        isFreeTier ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {renderGrid(sortFilter(modes), false, { serverFiltered: true })}
          </div>
        ) : (
          (() => {
            const sub = sortFilter(subModes);
            const main = sortFilter(mainModes);
            if (!sub.length && !main.length) {
              return (
                <p className="text-white/55 text-center py-8">لا توجد فئات</p>
              );
            }
            return (
              <>
                {sub.length > 0 && (
                  <div className="mb-5">
                    <h3 className="text-center text-white/70 text-sm font-bold mb-2">
                      مخصصة
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                      {renderGrid(sub, false, { serverFiltered: true })}
                    </div>
                  </div>
                )}
                {main.length > 0 && (
                  <div>
                    <h3 className="text-center text-white/70 text-sm font-bold mb-2">
                      أساسية
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                      {renderGrid(main, false, { serverFiltered: true })}
                    </div>
                  </div>
                )}
              </>
            );
          })()
        )
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {activeTab === "favorites" &&
              renderGrid(sortFilter(favorites), false, {
                serverFiltered: true,
              })}
            {activeTab === "custom" &&
              renderGrid(customModes, true, { serverFiltered: true })}
          </div>
          {activeTab === "custom" && (
            <>
              <div ref={sentinelRef} className="h-8" />
              {isFetchingNextPage && (
                <div className="flex justify-center py-3 text-white/80">
                  <Spinner size={20} />
                </div>
              )}
            </>
          )}
        </>
      )}

      {infoMode && (
        <Modal open onClose={() => setInfoMode(null)} title={infoMode.name} size="sm">
          <div className="flex items-start gap-3 mb-4">
            {infoMode.image && (
              <img
                src={infoMode.image}
                alt=""
                className="w-12 h-12 rounded-lg object-cover border border-white/10 shrink-0"
              />
            )}
            <div>
              <h3 className="font-bold">عدد الألعاب المتبقية</h3>
              <p className="text-white/70 text-sm mt-0.5">
                عدد الألعاب المتبقية قبل بدء تكرار الأسئلة
              </p>
            </div>
          </div>

          <div className="space-y-2 text-sm rounded-xl bg-white/[0.05] border border-white/10 p-3.5">
            <Row
              label="الألعاب المتبقية"
              value={`${infoMode.gamesLeft ?? 0} لعبة`}
            />
            {infoMode.isCustom ? (
              <>
                {infoMode.invitationCode && (
                  <Row label="كود الدعوة" value={infoMode.invitationCode} />
                )}
                <Row
                  label="عدد الأسئلة"
                  value={`${
                    infoMode.totalQuestions ?? infoMode.questionsCount ?? 0
                  } سؤال`}
                />
                <Row
                  label="عدد الإعجابات"
                  value={`${infoMode.likesCount ?? 0} إعجاب`}
                />
              </>
            ) : (
              infoMode.info && (
                <p className="text-white/70 pt-1">{infoMode.info}</p>
              )
            )}
          </div>

          <Button
            variant="secondary"
            block
            className="mt-5"
            onClick={() => setInfoMode(null)}
          >
            إغلاق
          </Button>
        </Modal>
      )}
    </div>
  );
};

export default ModePicker;
